import type { FormEvent } from 'react'

import { useCallback, useMemo, useState } from 'react'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type {
  ListDepartmentsParams,
  ListDirectoratesParams,
  ListSupervisorsParams,
  SupervisorAssignmentCreateDto,
  SupervisorAssignmentCreateDtoSupervisorLevel,
  SupervisorAssignmentDto,
} from '@/utils/api/user-service/models'

import { UserSearchInput } from '@/components/common/UserSearchInput'
import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AnimatedSwitch } from '@/components/ui/animated-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { handleError } from '@/lib/utils'
import {
  useCreateSupervisor,
  useDeleteSupervisor,
  useListDepartments,
  useListDirectorates,
  useListSupervisors,
  useUpdateSupervisor,
} from '@/utils/api/user-service'

interface SupervisorFormState {
  id: number | undefined
  departmentId: string
  directorateId: string
  supervisorUserId: number | undefined
  hrApprover: boolean
  ceoApprover: boolean
  supervisorName: string
  supervisorEmail: string
  supervisorLevel: '1' | '2' | undefined
  active: boolean
}

const DEFAULT_FORM_STATE: SupervisorFormState = {
  id: undefined as number | undefined,
  departmentId: '',
  directorateId: '',
  supervisorUserId: undefined as number | undefined,
  hrApprover: false,
  ceoApprover: false,
  supervisorName: '',
  supervisorEmail: '',
  supervisorLevel: '1',
  active: true,
}

export function SupervisorsPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)

  const departmentsParams = useMemo<ListDepartmentsParams>(() => ({
    page: 0,
    size: 100,
  }), [])
  const directoratesParams = useMemo<ListDirectoratesParams>(() => ({
    page: 0,
    size: 100,
  }), [])

  const supervisorsParams = useMemo<ListSupervisorsParams>(() => (
    { page: 0, size: 25 }
  ), [])

  const departmentsQuery = useListDepartments(departmentsParams)
  const directoratesQuery = useListDirectorates(directoratesParams)
  const supervisorsQuery = useListSupervisors(supervisorsParams)

  const createMutation = useCreateSupervisor({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogOpen(false)
        void supervisorsQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to create supervisor assignment. Please try again.'),
    },
  })

  const deleteMutation = useDeleteSupervisor({
    mutation: {
      onSuccess: () => {
        void supervisorsQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to delete supervisor assignment. Please try again.'),
    },
  })
  const updateMutation = useUpdateSupervisor({
    mutation: {
      onSuccess: () => {
        setFormState(DEFAULT_FORM_STATE)
        setDialogMode('create')
        setDialogOpen(false)
        void supervisorsQuery.refetch()
      },
      onError: (e: import('axios').AxiosError) => handleError(e, 'Unable to update supervisor assignment. Please try again.'),
    },
  })

  const departments = useMemo(() => departmentsQuery.data?.data?.content ?? [], [departmentsQuery.data])
  const directorates = useMemo(() => directoratesQuery.data?.data?.content ?? [], [directoratesQuery.data])
  const supervisors = useMemo(() => supervisorsQuery.data?.data?.content ?? [], [supervisorsQuery.data])

  const isHrApprover = formState.hrApprover
  const isCeoApprover = formState.ceoApprover
  const isSpecialApprover = isHrApprover || isCeoApprover
  const hasRoleSelection = (formState.supervisorLevel?.trim() ?? '') !== '' || isHrApprover || isCeoApprover
  const requiresDirectorate = !isSpecialApprover && formState.supervisorLevel === '2'
  const isSaving = createMutation.isPending || updateMutation.isPending
  const canSubmit = (requiresDirectorate
    ? formState.directorateId.trim() !== ''
    : formState.departmentId.trim() !== '')
  && formState.supervisorUserId !== undefined
  && hasRoleSelection

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || isSaving) {
      return
    }

    const parsedDeptId = Number(formState.departmentId)
    const parsedDirectorateId = Number(formState.directorateId)
    if (!hasRoleSelection) {
      return
    }

    const parsedSupervisorLevel = (formState.supervisorLevel === '1' || formState.supervisorLevel === '2')
      ? formState.supervisorLevel
      : undefined

    if (
      (!requiresDirectorate && Number.isNaN(parsedDeptId))
      || (requiresDirectorate && Number.isNaN(parsedDirectorateId))
      || ((!isHrApprover && !isCeoApprover) && parsedSupervisorLevel === undefined)
      || formState.supervisorUserId === undefined
    ) {
      return
    }

    const roleTitleMap: Record<'1' | '2', string> = {
      1: 'Manager',
      2: 'Director',
    }
    const roleTitle = isCeoApprover
      ? 'CEO'
      : isHrApprover
        ? 'HR'
        : (formState.supervisorLevel ? roleTitleMap[formState.supervisorLevel] : undefined)

    const payload: SupervisorAssignmentCreateDto = {
      departmentId: requiresDirectorate ? undefined : parsedDeptId,
      directorateId: requiresDirectorate ? parsedDirectorateId : undefined,
      supervisorUserId: formState.supervisorUserId,
      supervisorLevel: !isSpecialApprover && (formState.supervisorLevel?.trim() ?? '') !== ''
        ? parsedSupervisorLevel as SupervisorAssignmentCreateDtoSupervisorLevel
        : undefined,
      hrApprover: isHrApprover,
      ceoApprover: isCeoApprover,
      roleTitle: roleTitle ?? undefined,
      active: formState.active,
    }

    if (dialogMode === 'edit' && typeof formState.id === 'number') {
      updateMutation.mutate({ id: formState.id, data: payload })
      return
    }

    createMutation.mutate({ data: payload })
  }

  const openCreateDialog = useCallback(() => {
    setFormState(DEFAULT_FORM_STATE)
    setDialogMode('create')
    setDialogOpen(true)
  }, [])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setFormState(DEFAULT_FORM_STATE)
      setDialogMode('create')
    }
  }, [])

  const handleEdit = useCallback((item: SupervisorAssignmentDto) => {
    const supervisorLevel = item.supervisorLevel?.toString()
    const roleValue = item.hrApprover || item.ceoApprover
      ? undefined
      : supervisorLevel === '2'
        ? '2'
        : supervisorLevel === '1'
          ? '1'
          : undefined
    setFormState({
      id: item.id,
      departmentId: typeof item.departmentId === 'number' ? String(item.departmentId) : '',
      directorateId: typeof item.directorateId === 'number' ? String(item.directorateId) : '',
      supervisorUserId: item.supervisorUserId,
      hrApprover: item.hrApprover ?? false,
      ceoApprover: item.ceoApprover ?? false,
      supervisorName: item.supervisorName ?? '',
      supervisorEmail: item.supervisorEmail ?? '',
      supervisorLevel: roleValue,
      active: item.active ?? true,
    })
    setDialogMode('edit')
    setDialogOpen(true)
  }, [])

  const handleDelete = useCallback((item: SupervisorAssignmentDto) => {
    if (!item.id || deleteMutation.isPending) {
      return
    }
    deleteMutation.mutate({ id: item.id })
  }, [deleteMutation])

  const columns = useMemo<ColumnConfig<SupervisorAssignmentDto>[]>(() => ([
    {
      key: 'assignment',
      header: 'Assignment',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        if (row.directorateId) {
          return row.directorateName ?? row.directorateCode ?? `Directorate ${row.directorateId}`
        }
        if (row.departmentId) {
          return row.departmentName ?? row.departmentCode ?? `Dept ${row.departmentId}`
        }
        return 'N/A'
      },
    },
    {
      key: 'supervisorName',
      header: 'Supervisor',
      cell: (value, row) => {
        return value
          ? (
            <div>
              <div className="font-medium">{String(value)}</div>
              <div className="text-xs text-muted-foreground">
                {row.supervisorEmail}
              </div>
            </div>
          )
          : 'N/A'
      },
    },
    {
      key: 'supervisorLevel',
      header: 'Supervisor Role',
      cell: (value, row) => {
        if (row.ceoApprover) {
          return 'CEO'
        }
        if (row.hrApprover) {
          return 'HR'
        }
        const levelMap: Record<string, string> = {
          1: 'Manager',
          2: 'Director',
        }
        return levelMap[String(value)] ?? String(value)
      },
    },
    {
      key: 'active',
      header: 'Active',
      type: 'boolean',
    },
    {
      key: 'supervisorOrganization',
      header: 'Organization',
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={!row.id || deleteMutation.isPending}
            onClick={() => handleDelete(row)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ]), [deleteMutation.isPending, handleDelete, handleEdit])

  const dialogTitle = dialogMode === 'edit' ? 'Edit Supervisor Assignment' : 'Add Supervisor Assignment'
  const dialogDescription = dialogMode === 'edit'
    ? 'Update the selected supervisor assignment.'
    : 'Assign a supervisor to a department or directorate with their approval level.'
  const submitLabel = dialogMode === 'edit' ? 'Save Changes' : 'Add Supervisor'

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {dialogDescription}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                <div>
                  <Label htmlFor="supervisor-hr-approver">HR Approver</Label>
                  <p className="text-sm text-muted-foreground">Mark as HR approval supervisor.</p>
                </div>
                <AnimatedSwitch
                  id="supervisor-hr-approver"
                  checked={formState.hrApprover}
                  onCheckedChange={checked => setFormState(prev => ({
                    ...prev,
                    hrApprover: checked,
                    ceoApprover: checked ? false : prev.ceoApprover,
                    supervisorLevel: checked
                      ? undefined
                      : (prev.supervisorLevel?.trim() === '' || prev.supervisorLevel === undefined ? '1' : prev.supervisorLevel),
                  }))}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                <div>
                  <Label htmlFor="supervisor-ceo-approver">CEO Approver</Label>
                  <p className="text-sm text-muted-foreground">Mark as CEO approval supervisor.</p>
                </div>
                <AnimatedSwitch
                  id="supervisor-ceo-approver"
                  checked={formState.ceoApprover}
                  onCheckedChange={checked => setFormState(prev => ({
                    ...prev,
                    ceoApprover: checked,
                    hrApprover: checked ? false : prev.hrApprover,
                    supervisorLevel: checked
                      ? undefined
                      : (prev.supervisorLevel?.trim() === '' || prev.supervisorLevel === undefined ? '1' : prev.supervisorLevel),
                  }))}
                />
              </div>
            </div>

            {!isSpecialApprover && (
              <div className="space-y-2">
                <SearchSelect
                  id="supervisor-level"
                  label="Supervisor Level"
                  placeholder="Search and select supervisor level..."
                  value={formState.supervisorLevel ?? ''}
                  onValueChange={value => setFormState(prev => ({
                    ...prev,
                    supervisorLevel: value === '1' || value === '2' ? value : undefined,
                  }))}
                  options={[{
                    value: '1',
                    id: '1',
                    name: 'Manager',
                  }, {
                    value: '2',
                    id: '2',
                    name: 'Director',
                  }]}
                />
              </div>
            )}

            <div className="space-y-2">
              <SearchSelect
                id={requiresDirectorate ? 'supervisor-directorate' : 'supervisor-department'}
                label={requiresDirectorate ? 'Directorate' : 'Department'}
                placeholder={requiresDirectorate ? 'Search and select directorate...' : 'Search and select department...'}
                value={requiresDirectorate ? formState.directorateId : formState.departmentId}
                onValueChange={value => setFormState(prev => ({
                  ...prev,
                  directorateId: requiresDirectorate ? value : prev.directorateId,
                  departmentId: requiresDirectorate ? prev.departmentId : value,
                }))}
                options={(requiresDirectorate ? directorates : departments).map(item => ({
                  id: item.id?.toString() ?? '',
                  name: String(item.name ?? item.code ?? item.id ?? ''),
                }))}
                isLoading={requiresDirectorate ? directoratesQuery.isPending : departmentsQuery.isPending}
                disabled={requiresDirectorate
                  ? directoratesQuery.isPending || directoratesQuery.isError
                  : departmentsQuery.isPending || departmentsQuery.isError}
              />
            </div>

            <UserSearchInput
              id="supervisor-user"
              label="Supervisor"
              placeholder="Search supervisor by email..."
              required
              value={formState.supervisorUserId}
              prefilledUser={formState.supervisorUserId
                ? {
                  id: formState.supervisorUserId,
                  name: formState.supervisorName || undefined,
                  email: formState.supervisorEmail || undefined,
                }
                : undefined}
              onValueChange={userId => setFormState(prev => ({
                ...prev,
                supervisorUserId: userId,
                supervisorName: userId === prev.supervisorUserId ? prev.supervisorName : '',
                supervisorEmail: userId === prev.supervisorUserId ? prev.supervisorEmail : '',
              }))}
            />

            <div className="flex items-center space-x-2">
              <AnimatedSwitch
                id="supervisor-active"
                checked={formState.active}
                onCheckedChange={checked => setFormState(prev => ({ ...prev, active: checked }))}
              />
              <Label htmlFor="supervisor-active">Active</Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || isSaving}>
                {isSaving ? 'Saving...' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Supervisors</CardTitle>
            <CardDescription>Manage supervisor assignments for departments and directorates.</CardDescription>
          </div>
          <Button onClick={openCreateDialog}>
            Add Supervisor
          </Button>
        </CardHeader>
        <CardContent>
          {supervisorsQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Loading supervisors...</p>
            </div>
          )}
          {supervisorsQuery.isError && (
            <Alert variant="destructive">
              <AlertTitle>Unable to load supervisors</AlertTitle>
              <AlertDescription>{supervisorsQuery.error?.message}</AlertDescription>
            </Alert>
          )}
          {!supervisorsQuery.isLoading && !supervisorsQuery.isError && (
            <EnhancedDataTable
              data={supervisors}
              columns={columns}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
}
